import React, { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import FilterDropdown from './FilterDropdown'
import { connect } from 'react-redux'
import getOfferListKey from 'lib/offer/offerListKey'
import { queryKeyAmenities, queryKeyHolidayTypes, queryKeyInclusions } from 'constants/url'
import CustomerRatingDropdown from './CustomerRatingDropdown'
import InclusionsDropdown from './InclusionsDropdown'
import Carousel from 'LuxKit/Carousel/Carousel'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useInView } from 'react-intersection-observer'
import { OfferListSortOption } from 'constants/offerListFilters'
import { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { useScreenSizeOnly } from '../../../../hooks/useScreenSize'

const HeaderContainer = styled.div`
  position: sticky;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.default.eight};
  width: 100%;
  top: 0;

  &.lifted {
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  }
`

interface MappedStateProps {
  availableFilters: App.OfferListFilterOptions | undefined
  search: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>

interface Props extends InheritedOfferSortControlProps {
  filters: App.OfferListFilters
  showSort?: boolean
  sortOptions?: Array<OfferListSortOption>
  overrideAvailableFilters?: App.OfferListFilterOptions
  onFilterChange: FilterControlChangeHandler
  excludeOfferTypesFromCount?: boolean
  largeFilterChipSize?: boolean
}

function HotelExposedFiltersHeader(props: Props & MappedStateProps) {
  const {
    availableFilters,
    overrideAvailableFilters,
    filters,
    search,
    showSort = false,
    sortOptions,
    onFilterChange,
    onSortChange,
    excludeOfferTypesFromCount = false,
    largeFilterChipSize = false,
  } = props
  const intl = useIntl()

  const filterOptions = overrideAvailableFilters ?? availableFilters
  const { toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const isMobile = useScreenSizeOnly('mobile')
  const showSortDropdown = !isMobile

  const [toLiftRef, liftInView] = useInView()

  const offerTypesCount = excludeOfferTypesFromCount ? 0 : (filters.offerTypes?.length === 1 ? 1 : 0)
  const filterCount = (filters.holidayTypes?.length ?? 0) + (filters.amenities?.length ?? 0) + (filters.inclusions?.length ?? 0) +
    (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0) + offerTypesCount

  const allFiltersText = intl.formatMessage({
    id: 'jo1yPXHFKk',
    defaultMessage: 'All filters',
    description: 'Label for button that opens all filters panel',
  })

  const customerRatingLabel = intl.formatMessage({
    id: '30nggQW3WJ',
    defaultMessage: 'Customer rating',
    description: 'Label for customer rating filter dropdown',
  })

  const inclusionsLabel = intl.formatMessage({
    id: 'NroY+FENFB',
    defaultMessage: 'Inclusions',
    description: 'Label for inclusions filter dropdown',
  })

  const typeOfEscapeLabel = intl.formatMessage({
    id: 'WWQ1FYEOAg',
    defaultMessage: 'Type of escape',
    description: 'Label for holiday types filter dropdown',
  })

  const amenitiesLabel = intl.formatMessage({
    id: 'mz/qql7KcI',
    defaultMessage: 'Amenities',
    description: 'Label for amenities filter dropdown',
  })

  return (
    <>
      <div ref={toLiftRef} />
      <HeaderContainer className={!liftInView ? 'lifted' : ''}>
        <Carousel gap={8} gutterStyle="overflow">
          <FilterChip
            size={largeFilterChipSize ? 'large' : 'medium'}
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
            selected={filterCount > 0}
          >
            {allFiltersText} {filterCount ? `(${filterCount})` : ''}
          </FilterChip>
          {showSort && !!sortOptions?.length && (
            <OfferSortControl
              sortOptions={sortOptions}
              roundedButton
              showSelectedHighlight
              showDropdown={showSortDropdown}
              onSortChange={onSortChange}
            />
          )}
          <>
            <CustomerRatingDropdown largeFilterChipSize={largeFilterChipSize} filters={filters} search={search} label={customerRatingLabel} onFilterChange={onFilterChange} />
            <InclusionsDropdown
              largeFilterChipSize={largeFilterChipSize}
              searchQueryKey={queryKeyInclusions}
              label={inclusionsLabel}
              availableFilters={filterOptions?.filters?.inclusions}
              selectedFilters={filters?.inclusions ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            <FilterDropdown
              largeFilterChipSize={largeFilterChipSize}
              searchQueryKey={queryKeyHolidayTypes}
              label={typeOfEscapeLabel}
              availableFilters={filterOptions?.filters?.holidayTypes}
              filterOrder={filterOptions?.filterOrder?.holidayTypes}
              selectedFilters={filters?.holidayTypes ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
            <FilterDropdown
              largeFilterChipSize={largeFilterChipSize}
              searchQueryKey={queryKeyAmenities}
              label={amenitiesLabel}
              availableFilters={filterOptions?.filters?.amenities}
              filterOrder={filterOptions?.filterOrder?.amenities}
              selectedFilters={filters?.amenities ?? []}
              search={search}
              onFilterChange={onFilterChange}
            />
          </>
        </Carousel>
      </HeaderContainer>
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState, ownProps): MappedStateProps => {
    const key = getOfferListKey(ownProps.filters)
    const availableFilters = appState.offer.offerListFilterOptions
    return {
      availableFilters: availableFilters[key],
      search: appState.router.location.search,
    }
  },
)(HotelExposedFiltersHeader)
